import { X, Trash2, Minus, Plus, ShoppingCart, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config/api';

export function CartDrawer() {
  const { items, removeItem, updateQuantity, clearCart, totalItems, totalPrice, isOpen, setIsOpen } = useCart();
  const [checking, setChecking] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setChecking(true);
    try {
      const email = window.prompt('Enter your email for the receipt:');
      if (!email) { setChecking(false); return; }
      const cartItems = items.map(i => ({
        item_type: i.item_type,
        item_id: i.item_id,
        quantity: i.quantity || 1,
        license_tier: i.license_tier || null,
        size: i.size || null
      }));
      const { data } = await axios.post(`${API}/cart/checkout`, {
        items: cartItems,
        email,
        origin_url: window.location.origin
      });
      clearCart();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    }
    setChecking(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={() => setIsOpen(false)} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#141414] border-l border-[#262626] z-50 flex flex-col" data-testid="cart-drawer">
        <div className="flex items-center justify-between p-4 border-b border-[#262626]">
          <h2 className="font-cabinet font-black uppercase tracking-tight flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#DAA520]" />
            CART ({totalItems})
          </h2>
          <button onClick={() => setIsOpen(false)} className="text-[#A3A3A3] hover:text-white" data-testid="close-cart">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-[#262626] mx-auto mb-3" />
              <p className="text-[#A3A3A3] text-sm">Your cart is empty</p>
            </div>
          ) : (
            items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] p-3 rounded-lg" data-testid={`cart-item-${idx}`}>
                {item.cover_image && (
                  <img src={item.cover_image} alt={item.title} className="w-14 h-14 object-cover rounded shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{item.title}</p>
                  <p className="text-[10px] text-[#A3A3A3] uppercase">{item.item_type}{item.license_tier ? ` - ${item.license_tier}` : ''}{item.size ? ` - ${item.size}` : ''}</p>
                  <p className="text-sm font-mono text-[#DAA520]">${(item.price || 0).toFixed(2)}</p>
                </div>
                <div className="flex flex-col items-center gap-1 shrink-0">
                  {item.item_type === 'merch' && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateQuantity(idx, (item.quantity || 1) - 1)} className="w-6 h-6 flex items-center justify-center bg-[#262626] rounded text-xs hover:bg-[#333]" data-testid={`cart-qty-minus-${idx}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-mono w-6 text-center">{item.quantity || 1}</span>
                      <button onClick={() => updateQuantity(idx, (item.quantity || 1) + 1)} className="w-6 h-6 flex items-center justify-center bg-[#262626] rounded text-xs hover:bg-[#333]" data-testid={`cart-qty-plus-${idx}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <button onClick={() => removeItem(idx)} className="text-[#A3A3A3] hover:text-[#FF3B30]" data-testid={`cart-remove-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#262626] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3] uppercase">Total</span>
              <span className="text-xl font-bold font-mono text-[#DAA520]">${totalPrice.toFixed(2)}</span>
            </div>
            <Button onClick={handleCheckout} disabled={checking}
              className="w-full h-12 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] font-bold text-sm uppercase rounded-none" data-testid="cart-checkout-btn">
              {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PROCESSING...</> : `CHECKOUT - $${totalPrice.toFixed(2)}`}
            </Button>
            <button onClick={clearCart} className="w-full text-center text-xs text-[#A3A3A3] hover:text-[#FF3B30] uppercase" data-testid="cart-clear-btn">
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
